import { useMemo } from 'react';
import type {
  SubstepDefinition,
  MainStepDefinition,
} from '../../utils/stepDefinitions';
import type { ProgressSegment } from '../../components/SmartOnboardingProgressBar';

interface UseActivationProgressParams {
  visibleSubsteps: SubstepDefinition[];
  currentStepIndex: number;
  currentStepId: string;
  currentMainStep?: MainStepDefinition; // Current main step to filter substeps
  completedStepIds?: string[]; // Optional: explicitly mark certain steps as completed
  disableNavigation?: boolean; // Disable all clickable segments (e.g., when step was auto-skipped)
}

interface UseActivationProgressReturn {
  progressSegments: ProgressSegment[];
  currentStepNumber: number;
  totalSteps: number;
  percentage: number;
}

/**
 * Hook to generate progress segments for the smart progress bar
 * Automatically calculates which steps are completed, current, and clickable
 *
 * Logic:
 * - Only shows substeps within the current main step
 * - All steps before current are marked as completed and clickable
 * - Current step is marked as current (in progress)
 * - Future steps are not completed and not clickable
 * - If disableNavigation is true, all segments become non-clickable (used for auto-skipped steps)
 */
export const useActivationProgress = ({
  visibleSubsteps,
  currentStepIndex,
  currentStepId,
  currentMainStep,
  completedStepIds = [],
  disableNavigation = false,
}: UseActivationProgressParams): UseActivationProgressReturn => {
  const progressSegments = useMemo<ProgressSegment[]>(() => {
    // Filter to only substeps within the current main step
    const mainStepSubsteps = currentMainStep
      ? currentMainStep.substeps.filter((substep) =>
          visibleSubsteps.some((vs) => vs.id === substep.id),
        )
      : visibleSubsteps;

    // Find the index of the current step within the main step's substeps
    const currentIndexInMainStep = mainStepSubsteps.findIndex(
      (substep) => substep.id === currentStepId,
    );

    return mainStepSubsteps.map((substep, index) => {
      const isCompleted =
        index < currentIndexInMainStep || completedStepIds.includes(substep.id);
      const isCurrent = index === currentIndexInMainStep;

      // TODO: Backward navigation temporarily disabled
      // Will be re-enabled later with proper validation
      // const isClickable = isCompleted && !isCurrent && !disableNavigation;
      const isClickable = false;

      return {
        id: substep.id,
        label: substep.titleKey, // Could be used for tooltips
        isCompleted,
        isCurrent,
        isClickable,
      };
    });
  }, [
    visibleSubsteps,
    currentStepIndex,
    currentStepId,
    currentMainStep,
    completedStepIds,
    disableNavigation,
  ]);

  const currentStepNumber = progressSegments.findIndex((s) => s.isCurrent) + 1;
  const totalSteps = progressSegments.length;

  // Guard against division by zero (empty segments) and missing current step
  const percentage =
    totalSteps > 0 && currentStepNumber > 0
      ? Math.round((currentStepNumber / totalSteps) * 100)
      : 0;

  return {
    progressSegments,
    currentStepNumber,
    totalSteps,
    percentage,
  };
};
